import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Globe, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WebsitePreview = () => {
  const { websiteId } = useParams();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const response = await axios.get(`${API}/websites/preview/${websiteId}`);
        setWebsite(response.data);
      } catch (err) {
        setError('Website not found or preview expired');
      } finally {
        setLoading(false);
      }
    };

    fetchPreview();
  }, [websiteId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading preview...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md px-4">
          <Globe className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Preview Not Available</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/">
            <Button>Back to TryWeb</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Banner */}
      <div className="bg-primary text-white py-3 px-4 text-center">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-sm">
            <strong>Free Preview:</strong> This is a preview of your website from TryWeb
          </p>
          <Link to={`/order/${website?.subdomain || websiteId}`}>
            <Button size="sm" variant="secondary" className="gap-1" data-testid="order-btn">
              Order Now <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Website Preview */}
      <div className="w-full">
        <iframe
          srcDoc={`
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <base target="_self">
              <style>${website?.css || ''}</style>
              <script>
                document.addEventListener('DOMContentLoaded', function() {
                  // Handle anchor links - smooth scroll within iframe
                  document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
                    anchor.addEventListener('click', function(e) {
                      e.preventDefault();
                      var targetId = this.getAttribute('href').substring(1);
                      if (targetId) {
                        var targetEl = document.getElementById(targetId);
                        if (targetEl) {
                          targetEl.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    });
                  });
                  
                  // Prevent external links from navigating
                  document.querySelectorAll('a:not([href^="#"])').forEach(function(link) {
                    link.addEventListener('click', function(e) {
                      e.preventDefault();
                    });
                  });
                });
              </script>
            </head>
            <body>${website?.html || '<p>No content available</p>'}</body>
            </html>
          `}
          className="w-full min-h-screen border-0"
          title="Website Preview"
        />
      </div>
    </div>
  );
};

export default WebsitePreview;
